/**
 * Apply agent / WebSocket wishlist actions to WishlistContext (local state only).
 * Used by typed chat, voice/avatar, and any OrbieeFlow caller — no REST API.
 */

import {
  beginAgentAction,
  endAgentAction,
  markAgentNavigation,
} from "../services/uiAgentSync";

const WISHLIST_SYNC_KEY = "orbiee_wishlist_synced_at";

export function stampWishlistAgentSync() {
  try {
    sessionStorage.setItem(WISHLIST_SYNC_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ mergeItemIntoWishlist: (item: object) => void, removeFromWishlist?: (id: string) => void }} wishlist
 * @param {object | null | undefined} lastResponse
 * @param {{ candidateProducts?: object[], navigate?: (path: string) => void }} [opts]
 * @returns {{ handled: boolean, toast: string | null }}
 */
export function applyWishlistFromAgentResponse(lastResponse, wishlist, opts = {}) {
  sessionStorage.setItem("orbiee_virtual_try_on_open", "false")
  const navigate = opts.navigate;
  const goToWishlist =
    typeof navigate === "function"
      ? () => {
          // Agent-driven navigation — tag the resulting view_changed event
          markAgentNavigation();
          navigate("/wishlist");
        }
      : null;
  const mergeItemIntoWishlist = wishlist?.mergeItemIntoWishlist;
  const removeFromWishlist = wishlist?.removeFromWishlist;
  if (typeof mergeItemIntoWishlist !== "function") {
    return { handled: false, toast: null };
  }

  const action = lastResponse?.action;
  const conversationText = lastResponse?.conversation_text || "";
  const products = Array.isArray(lastResponse?.products)
    ? lastResponse.products
    : [];
  const candidateProducts =
    Array.isArray(opts.candidateProducts) && opts.candidateProducts.length
      ? opts.candidateProducts
      : products;

  let handled = false;
  let toast = null;

  if (action && typeof action === "object") {
    if (action.type === "ADD_TO_WISHLIST" && action.status === "SUCCESS") {
      if (Array.isArray(action.wishlist_data) && action.wishlist_data.length > 0) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
        handled = true;
        toast = "Added to wishlist";
        goToWishlist?.();
      } else if (action.product && (action.product.id ?? action.article_id)) {
        const productToAdd = action.product.id
          ? action.product
          : {
              ...action.product,
              id: action.article_id,
              article_id: action.article_id,
            };
        mergeItemIntoWishlist(productToAdd);
        handled = true;
        toast = "Added to wishlist";
        goToWishlist?.();
      } else {
        stampWishlistAgentSync();
      }
    } else if (action.type === "VIEW_WISHLIST") {
      if (Array.isArray(action.wishlist_data) && action.wishlist_data.length > 0) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
      }
      handled = true;
      stampWishlistAgentSync();
    } else if (action.type === "REMOVE_FROM_WISHLIST" && action.status === "SUCCESS") {
      if (action.article_id && typeof removeFromWishlist === "function") {
        // Agent-driven removal — tag the ui_event as source: "agent" (echo suppression)
        beginAgentAction();
        try {
          removeFromWishlist(action.article_id);
        } finally {
          endAgentAction();
        }
        handled = true;
        toast = "Removed from wishlist";
      } else if (Array.isArray(action.wishlist_data)) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
        handled = true;
        toast = "Removed from wishlist";
      }
      stampWishlistAgentSync();
    }
  }

  if (!handled && lastResponse?.wishlist != null) {
    const rawWl = lastResponse.wishlist;
    if (
      rawWl != null &&
      typeof rawWl === "object" &&
      Array.isArray(rawWl.items) &&
      rawWl.items.length > 0
    ) {
      rawWl.items.forEach((item) => mergeItemIntoWishlist(item));
      handled = true;
      toast = "Added to wishlist";
      stampWishlistAgentSync();
      goToWishlist?.();
    }
  }

  if (!handled && conversationText) {
    const wishlistAddPattern =
      /added.*wishlist|wishlist.*added|saved.*wishlist|already on your wishlist|i['']ve added.*wishlist|on your wishlist/i;
    if (wishlistAddPattern.test(conversationText) && candidateProducts.length > 0) {
      mergeItemIntoWishlist(candidateProducts[0]);
      handled = true;
      toast = "Added to wishlist";
      stampWishlistAgentSync();
      goToWishlist?.();
    }

    const wishlistViewPattern =
      /here['']s what['']s on your wishlist|your wishlist|what['']s on your wishlist|items? (saved|in) (your )?wishlist|in your wishlist|on your wishlist|wishlist.*item|saved.*wishlist/i;
    if (wishlistViewPattern.test(conversationText) && candidateProducts.length > 0) {
      candidateProducts.forEach((p) =>
        mergeItemIntoWishlist({
          id: p.id ?? p.article_id,
          article_id: p.article_id ?? p.id,
          title: p.title ?? p.name ?? "",
          name: p.title ?? p.name ?? "",
          price: p.price ?? p.price_info?.price ?? 0,
          image: p.image ?? (Array.isArray(p.images) ? p.images[0] : ""),
          image_uri: p.image_uri ?? p.image,
        })
      );
      handled = true;
      stampWishlistAgentSync();
    }
  }

  if (handled && toast) {
    stampWishlistAgentSync();
  }

  return { handled, toast };
}
